'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface StatisticItem {
  id: string;
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

const statistics: StatisticItem[] = [
  { id: 'scams', value: 34, suffix: '%', label: 'av nordmenn har opplevd eller kjenner noen som har opplevd svindel ved gullsalg', icon: 'ExclamationTriangleIcon' },
  { id: 'unfair', value: 47, suffix: '%', label: 'føler de ikke fikk rettferdig pris for sitt gull', icon: 'CurrencyDollarIcon' },
  { id: 'avoid', value: 62, suffix: '%', label: 'unngår å selge gull på grunn av manglende tillit', icon: 'ShieldExclamationIcon' },
];

const ProblemSection = () => {
  const [animatedValues, setAnimatedValues] = useState<{ [key: string]: number }>({});
  const [hasAnimated, setHasAnimated] = useState<boolean>(false);
  const [isHydrated, setIsHydrated] = useState<boolean>(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateCounters();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated, isHydrated]);

  const animateCounters = () => {
    statistics.forEach((stat) => {
      const duration = 2000;
      const steps = 60;
      const increment = stat.value / steps;
      let currentValue = 0;
      let step = 0;

      const timer = setInterval(() => {
        step++;
        currentValue += increment;

        if (step >= steps) {
          currentValue = stat.value;
          clearInterval(timer);
        }

        setAnimatedValues((prev) => ({
          ...prev,
          [stat.id]: currentValue,
        }));
      }, duration / steps);
    });
  };

  if (!isHydrated) {
    return (
      <section className="bg-muted py-12 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="font-headline text-3xl font-bold text-foreground lg:text-4xl">
              Hvorfor er det så vanskelig å selge gull trygt?
            </h2>
            <p className="mt-4 font-body text-lg text-muted-foreground lg:text-xl">
              Mange nordmenn opplever utfordringer når de skal selge sitt gull
            </p>
          </div>
          <div className="grid gap-8 lg:grid-cols-3">
            {statistics.map((stat) => (
              <div key={stat.id} className="rounded-lg bg-card p-8 text-center shadow-md">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                  <Icon name={stat.icon as any} size={32} variant="solid" className="text-destructive" />
                </div>
                <div className="font-headline text-4xl font-bold text-destructive lg:text-5xl">
                  {stat.value}{stat.suffix}
                </div>
                <p className="mt-4 font-body text-sm text-muted-foreground lg:text-base">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="bg-muted py-12 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="font-headline text-3xl font-bold text-foreground lg:text-4xl">
            Hvorfor er det så vanskelig å selge gull trygt?
          </h2>
          <p className="mt-4 font-body text-lg text-muted-foreground lg:text-xl">
            Mange nordmenn opplever utfordringer når de skal selge sitt gull
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {statistics.map((stat) => (
            <div
              key={stat.id}
              className="rounded-lg bg-card p-8 text-center shadow-md transition-transform duration-300 hover:scale-105"
            >
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <Icon name={stat.icon as any} size={32} variant="solid" className="text-destructive" />
              </div>
              <div className="font-headline text-4xl font-bold text-destructive lg:text-5xl">
                {Math.floor(animatedValues[stat.id] || 0)}{stat.suffix}
              </div>
              <p className="mt-4 font-body text-sm text-muted-foreground lg:text-base">
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-lg bg-destructive/10 p-6 lg:p-8">
          <div className="flex flex-col items-center space-y-4 text-center lg:flex-row lg:space-x-6 lg:space-y-0 lg:text-left">
            <Icon name="ExclamationCircleIcon" size={48} variant="solid" className="text-destructive" />
            <div className="flex-1">
              <h3 className="font-headline text-xl font-semibold text-foreground lg:text-2xl">
                Du fortjener bedre
              </h3>
              <p className="mt-2 font-body text-base text-muted-foreground">
                Hos Gullklar får du full transparens, rettferdig pris, og trygghet i hver transaksjon. Vi er her for å gjøre gullsalg enkelt og trygt for alle nordmenn.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;